/** Create/Edit dialog for Events (full content fields + cover image). */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
        Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { CoverUpload } from '@/components/media/Cover';
import { TextField, TextArea, SelectField, toLocalInput, toISO } from './_formkit';
import { DASH } from '@/constants/testIds';

const EMPTY = {
        title: '', category: '', organizer: '', organizer_activity_id: '', location_name: '', address: '', contact: '',
        website: '', opening_hours: '', place_id: '', description: '', start_at: '', end_at: '',
        cover_media_id: null,
};

export function EventFormDialog({ open, onOpenChange, initial, places = [], activities = [], onSubmit, pending }) {
        const [f, setF] = useState(EMPTY);
        const isEdit = !!initial;

        useEffect(() => {
                if (!open) return;
                setF(initial ? {
                        title: initial.title || '', category: initial.category || '', organizer: initial.organizer || '', organizer_activity_id: initial.organizer_activity_id || '',
                        location_name: initial.location_name || '', address: initial.address || '', contact: initial.contact || '',
                        website: initial.website || '', opening_hours: initial.opening_hours || '', place_id: initial.place_id || '',
                        description: initial.description || '', start_at: toLocalInput(initial.start_at), end_at: toLocalInput(initial.end_at),
                        cover_media_id: initial.cover_media_id || null,
                } : EMPTY);
        }, [open, initial]);

        const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

        const submit = (e) => {
                e.preventDefault();
                onSubmit({
                        title: f.title,
                        category: f.category || undefined,
                        organizer: f.organizer || undefined,
                        organizer_activity_id: f.organizer_activity_id || null,
                        location_name: f.location_name || undefined,
                        address: f.address || undefined,
                        contact: f.contact || undefined,
                        website: f.website || undefined,
                        opening_hours: f.opening_hours || undefined,
                        place_id: f.place_id || undefined,
                        description: f.description || undefined,
                        cover_media_id: f.cover_media_id || undefined,
                        start_at: toISO(f.start_at),
                        end_at: f.end_at ? toISO(f.end_at) : undefined,
                });
        };

        return (
                <Dialog open={open} onOpenChange={onOpenChange}>
                        <DialogContent data-testid={DASH.createDialog} className="bg-slate-900 text-slate-100 border-slate-800 max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                        <DialogTitle>{isEdit ? 'Modifica evento' : 'Nuovo evento'}</DialogTitle>
                                        <DialogDescription className="text-slate-400">
                                                Lo stato (bozza/approvato/pubblicato) si gestisce dai pulsanti dedicati.
                                        </DialogDescription>
                                </DialogHeader>
                                <form onSubmit={submit} className="space-y-3">
                                        <CoverUpload previewUrl={initial?.cover_url} onChange={set('cover_media_id')} />
                                        <TextField label="Titolo" value={f.title} onChange={set('title')} required data-testid={DASH.titleInput} placeholder="Festa del Mare" />
                                        <div className="grid grid-cols-2 gap-3">
                                                <TextField label="Categoria" value={f.category} onChange={set('category')} placeholder="musica, sport…" />
                                                <TextField label="Organizzatore" value={f.organizer} onChange={set('organizer')} />
                                        </div>
                                        <SelectField label="Attività organizzatrice" value={f.organizer_activity_id} onChange={set('organizer_activity_id')}
                                                options={activities.map((a) => ({ value: a.id, label: a.title }))} placeholder="Nessuna attività" testId="event-organizer-activity-select" />
                                        <div className="grid grid-cols-2 gap-3">
                                                <TextField label="Inizio" type="datetime-local" value={f.start_at} onChange={set('start_at')} required data-testid={DASH.dateInput} />
                                                <TextField label="Fine" type="datetime-local" value={f.end_at} onChange={set('end_at')} data-testid="dashboard-event-end-input" />
                                        </div>
                                        <SelectField label="Luogo" value={f.place_id} onChange={set('place_id')}
                                                options={places.map((p) => ({ value: p.id, label: p.name }))} placeholder="Nessun luogo" />
                                        <div className="grid grid-cols-2 gap-3">
                                                <TextField label="Nome luogo" value={f.location_name} onChange={set('location_name')} />
                                                <TextField label="Indirizzo" value={f.address} onChange={set('address')} />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                                <TextField label="Contatti" value={f.contact} onChange={set('contact')} />
                                                <TextField label="Sito web" value={f.website} onChange={set('website')} />
                                        </div>
                                        <TextField label="Orari" value={f.opening_hours} onChange={set('opening_hours')} />
                                        <TextArea label="Descrizione" value={f.description} onChange={set('description')} rows={4} />
                                        <DialogFooter>
                                                <Button type="submit" disabled={pending} data-testid={isEdit ? DASH.submitEdit : DASH.submitCreate}
                                                        className="bg-emerald-400 text-slate-950 hover:bg-emerald-300">
                                                        {isEdit ? 'Salva modifiche' : 'Crea'}
                                                </Button>
                                        </DialogFooter>
                                </form>
                        </DialogContent>
                </Dialog>
        );
}
